import React from "react";
import { Bars3Icon, ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from "react-router-dom";
import { usePageBackTarget, usePageNavTarget, useTrackRoute } from "../../../hooks";
import { HeaderUserMenu } from "./HeaderUserMenu.component";
import { HeaderNotificationsMenu } from "./HeaderNotificationsMenu.component";

export interface AppHeaderProps {
    setSidebarOpen: (value: boolean) => void;
}

/**
 * Consumed by page-level sticky elements that must sit directly beneath the
 * header. Published as a CSS variable on the scroll container so those elements
 * can position themselves without importing header internals.
 */
const HEADER_OFFSET_VAR = '--app-header-offset';

// #region AppHeader
/**
 * The permanent header bar. It spans the content area only — it lives inside
 * `<main>`, which already carries the sidebar offset — so the sidebar keeps its
 * own full-height column and its logo.
 *
 * Height is deliberately minimal: tall enough for icon buttons plus padding and
 * nothing more. Unlike the mobile bar it replaced, it never hides on scroll,
 * because the back affordance and the account menu must always be reachable.
 */
export const AppHeader: React.FunctionComponent<AppHeaderProps> = (props: AppHeaderProps): JSX.Element => {
    const { setSidebarOpen } = props;
    const navigate = useNavigate();
    useTrackRoute();
    const pageBack = usePageBackTarget();
    const pageNav = usePageNavTarget();
    const barRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const bar = barRef.current;
        if (!bar) return;
        const scrollContainer = bar.closest('main');
        if (!scrollContainer) return;

        scrollContainer.style.setProperty(HEADER_OFFSET_VAR, `${bar.offsetHeight || 0}px`);
        return () => { scrollContainer.style.removeProperty(HEADER_OFFSET_VAR); };
    }, []);

    // #region handleBack
    const handleBack = () => {
        if (!pageBack) return;
        if (pageBack.onBack) {
            pageBack.onBack();
            return;
        }
        if (pageBack.href) {
            navigate(pageBack.href);
        }
    };

    return (
        <div
            ref={barRef}
            className="
                sticky top-0 z-40 flex items-center justify-between gap-x-3 px-3 py-2 sm:px-4
                border-b border-surface-border
                bg-surface-header
                print:hidden
            "
        >
            <div className="flex items-center gap-x-2 min-w-0">
                <button
                    type="button"
                    className="p-1.5 rounded-md lg:hidden dark:text-gray-300 text-gray-700 hover:bg-surface-muted"
                    onClick={() => setSidebarOpen(true)}
                >
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon className="h-6 w-6 text-brand dark:text-brand-dark" aria-hidden="true" />
                </button>

                {/*
                  The single home for every "Back to <x>" button in the app.
                  Pages register a target with `usePageBack`; nothing renders here
                  when the current page has no parent to return to.
                */}
                {pageBack ? (
                    <button
                        type="button"
                        onClick={handleBack}
                        aria-label={pageBack.label}
                        className="inline-flex items-center gap-x-2 px-2 py-1.5 rounded-md text-[13px] font-semibold dark:text-gray-200 text-gray-700 hover:bg-surface-muted cursor-pointer min-w-0"
                    >
                        <ArrowLeftIcon className="h-5 w-5 shrink-0 text-brand dark:text-brand-dark" aria-hidden="true" />
                        <span className="hidden sm:inline truncate">{pageBack.label}</span>
                        <span className="sm:hidden">{pageBack.shortLabel ?? 'Back'}</span>
                    </button>
                ) : null}

                {/*
                  Prev/Next across whatever list the user came from — registered
                  by the page with `usePageNav`, absent for pages that have no
                  siblings to walk (or that were opened cold, with no list in
                  memory). The position text is dropped below `sm`, mirroring
                  how the back button collapses to its short label.
                */}
                {pageNav ? (
                    <div className="flex items-center gap-x-0.5 shrink-0 border-l border-surface-border pl-2 ml-1">
                        <button
                            type="button"
                            onClick={pageNav.onPrev}
                            disabled={!pageNav.canPrev || pageNav.loading}
                            aria-label="Previous order"
                            className="p-1.5 rounded-md dark:text-gray-200 text-gray-700 hover:bg-surface-muted cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                            <ChevronLeftIcon className="h-5 w-5 shrink-0 text-brand dark:text-brand-dark" aria-hidden="true" />
                        </button>
                        <span className="hidden sm:inline whitespace-nowrap px-1 text-[13px] font-medium dark:text-gray-300 text-gray-600 tabular-nums">
                            {pageNav.index + 1} of {pageNav.total}
                        </span>
                        <button
                            type="button"
                            onClick={pageNav.onNext}
                            disabled={!pageNav.canNext || pageNav.loading}
                            aria-label="Next order"
                            className="p-1.5 rounded-md dark:text-gray-200 text-gray-700 hover:bg-surface-muted cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                            <ChevronRightIcon className="h-5 w-5 shrink-0 text-brand dark:text-brand-dark" aria-hidden="true" />
                        </button>
                    </div>
                ) : null}
            </div>

            <div className="flex items-center gap-x-1 shrink-0">
                <HeaderNotificationsMenu />
                <HeaderUserMenu />
            </div>
        </div>
    );
};
